// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MantineProvider, localStorageColorSchemeManager } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { AuthProvider } from './context/AuthContext';
import { BackgroundImageProvider } from './context/BackgroundImageContext';
import ProtectedRoute from './components/ProtectedRoute';
import InternalPageLayout from './components/Layout/InternalPageLayout';

// Import Pages
import LoginPage from './components/Auth/LoginPage';
import FICSPage from './components/Pages/FICSPage';
import BrochurewarePage from './components/Pages/BrochurewarePage';
import MenuPage from './components/Pages/MenuPage';
import StrategicTitlePage from './components/Pages/StrategicTitlePage';
import BriefBuilderPage from './components/Pages/BriefBuilderPage';
import GuidelinesPage from './components/Pages/GuidelinesPage';
import ManageBriefsPage from './components/Pages/ManageBriefsPage';
import DispatchedBriefsDashboard from './components/Pages/DispatchedBriefsDashboard';
import ManageCalendarPage from './components/Pages/ManageCalendarPage';
import AdminDataUploader from './components/Pages/AdminDataUploader';
import RefreshRadarDashboard from './components/Pages/RefreshRadarDashboard';
import ResellPipelinePage from './components/Pages/ResellPipelinePage';
import ResellPipelinePageZustand from './components/Pages/ResellPipelinePageZustand';
import ResellPreparationPage from './components/Pages/ResellPreparationPage';
import ResellSchedulingPage from './components/Pages/ResellSchedulingPage';
import ResellBlacklistPage from './components/Pages/ResellBlacklistPage';
import RefactorLabPage from './components/Pages/RefactorLabPage';
import VideoProducerLabPage from './components/Pages/VideoProducerLabPage';
import VideoLibraryPage from './components/Pages/VideoLibraryPage';
import KnowledgeGraphPage from './components/Pages/KnowledgeGraphPage';
import EpistemicAuditPage from './components/Pages/EpistemicAuditPage';


const yourBaseTheme = {
  fontFamily: 'Lora, Segoe UI, Roboto, Arial, sans-serif',
};

const colorSchemeManager = localStorageColorSchemeManager({
  key: 'mantine-app-color-scheme',
});

function App() {
  return (
    <MantineProvider
      theme={yourBaseTheme}
      defaultColorScheme="light"
      colorSchemeManager={colorSchemeManager}
      withGlobalStyles
      withNormalizeCSS
    >
      <Notifications position="top-right" style={{ top: 75, pointerEvents: 'none' }} />
      <AuthProvider>
        <BackgroundImageProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<BrochurewarePage />} />
              <Route path="/login" element={<LoginPage />} />

              {/* --- SHARED ROUTES (All Auth Users) --- */}
              <Route
                path="/menu"
                element={
                  <ProtectedRoute>
                    <InternalPageLayout>
                      <MenuPage />
                    </InternalPageLayout>
                  </ProtectedRoute>
                }
              />

              {/* --- EDITOR / WRITER ACCESS (Explicitly Open) --- */}
              <Route
                path="/resell-preparation"
                element={
                  <ProtectedRoute>
                    <InternalPageLayout>
                      <ResellPreparationPage />
                    </InternalPageLayout>
                  </ProtectedRoute>
                }
              />

              {/* --- RESTRICTED ROUTES (Admin / Management Only) --- */}
              <Route
                path="/fics"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><FICSPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategic-titles"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><StrategicTitlePage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brief-builder"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><BriefBuilderPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/guidelines"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><GuidelinesPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-briefs"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><ManageBriefsPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dispatched-briefs"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><DispatchedBriefsDashboard /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-calendar"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><ManageCalendarPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/data-upload"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><AdminDataUploader /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />


              {/* Legacy Pipelines - Locked Down */}
              <Route
                path="/resell-pipeline"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><ResellPipelinePage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resell-pipeline-v2"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout><ResellPipelinePageZustand /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />

              {/* --- REFACTOR ENGINE (Admin Only) --- */}
              <Route
                path="/lab/refactor"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <InternalPageLayout>
                      <RefactorLabPage />
                    </InternalPageLayout>
                  </ProtectedRoute>
                }
              />

              {/* --- VIDEO LIBRARY (Was Admin Only; open access as of 29/10/26) --- */}
              <Route
                path="/lab/video-producer"
                element={
                  <ProtectedRoute> {/* Removed allowedRoles restrictions */}
                    <InternalPageLayout>
                      <VideoProducerLabPage />
                    </InternalPageLayout>
                  </ProtectedRoute>
                }
              />

              {/* --- VIDEO LIBRARY (Was Admin Only; open access as of 29/10/26) --- */}
              <Route
                path="/lab/video-library"
                element={
                  <ProtectedRoute> {/* Removed allowedRoles restrictions */}
                    <InternalPageLayout>
                      <VideoLibraryPage />
                    </InternalPageLayout>
                  </ProtectedRoute>
                }
              />

              {/* New Scheduling View - Open to Editors */}
              <Route
                path="/resell-radar"
                element={
                  <ProtectedRoute> {/* Removed allowedRoles restrictions */}
                    <InternalPageLayout><RefreshRadarDashboard /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resell-scheduling"
                element={
                  <ProtectedRoute> {/* Removed allowedRoles */}
                    <InternalPageLayout><ResellSchedulingPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resell-blacklist"
                element={
                  <ProtectedRoute> {/* Removed allowedRoles */}
                    <InternalPageLayout><ResellBlacklistPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
              {/* 3D Epistemic Visualisation for Mgt */}
              <Route
                path="/knowledge-graph"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'management']}>
                    <KnowledgeGraphPage />
                  </ProtectedRoute>
                }
              />
              {/* Epistemic Prototype Showcase */}
              <Route
                path="/lab/epistemic"
                element={
                  <ProtectedRoute> {/* Removed allowedRoles */}
                    <InternalPageLayout><EpistemicAuditPage /></InternalPageLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </BackgroundImageProvider>
      </AuthProvider>
    </MantineProvider>
  );
}

export default App;